/* eslint-disable no-use-before-define */
import React, { useState, useCallback, useContext } from 'react';
import PropTypes from 'prop-types';
import _ from 'underscore';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import { LegacyI18nContext } from '@bingads-webui-react/legacy-i18n-context';
import { Col, Row } from '@bingads-webui-react/primitive';
import { useTermsAndConditions, withCacheProvider } from '@bingads-webui-campaign-react/cc-data-actions';
import { UrlBuilderContext } from '@bingads-webui-cc-react/url-builder-react';
import TermsAndConditionsComponent from '@bingads-webui-cc-react/terms-and-conditions';

import useFeatures from './use-features';

// #HACK: bypass some badly written code
// TODO: fix the code (useTermsAndConditions) in @bingads-webui-campaign-react/cc-data-actions
// TODO: maybe fix the amazingly convoluted logic in @bingads-webui-cc-react/terms-and-conditions (e.g. the agreements *should* be reset every time the country changes)
// TODO: maybe use local shimmer effects when the agreements are loading
const DummyContext = {
  CurrentAccount: true,
};

const EmptyArray = Object.freeze([]);

export default function TermsAndConditionsSection(props) {
  return (
    <Row className="terms-and-condition-section">
      <Col sm={12}>
        <TermsAndConditions {...props} />
      </Col>
    </Row>
  );
}

/**
 * Defines a form that handles the user's agreement to the application's terms and conditions.
 * This is always expected to execute within a Client Center API context;
 * at the minimum, the {@link UrlBuilderContext} must be defined when rendering it.
 * @param {Object} props - The component's props.
 * @param {string} props.country - The country code, which determines the terms and conditions in effect.
 * @param {function} props.onChange - The callback to invoke when the user's agreement has changed.
 * @param {boolean} props.showValidationResult - Whether to show a note that the terms must be accepted,
 * if the user tries to save the form without doing so. Usually this is set to true after the first such attempt.
 * @returns {Element} The component with the terms and conditions form.
 */
export const TermsAndConditions = withCacheProvider(({
  country,
  onChange,
  showValidationResult,
  isTAndCChecked,
  isDisabled,
  communication,
}) => {
  const legacyI18n = useContext(LegacyI18nContext);
  const i18n = useContext(I18nContext);
  const tc = useTermsAndConditions(country, DummyContext);
  // #HACK: monumental hack, to work around disjointed component API...
  // the only reason for requiring ccBaseUrl here is to get to TermsAndConditionsUrl,
  // which we should have been able to do anyway, since  we should be running in a UrlBuilderContext
  const { urlHelper: { TermsAndConditionsUrl } } = useContext(UrlBuilderContext);
  const [isMarketingCommunicationDefaultOptInEnabled] = useFeatures('MarketingCommunicationDefaultOptIn').values;

  const [isAccepted, setIsAccepted] = useState(false);
  const onTermsChange = useCallback(({
    isValid,
    options,
  }) => {
    setIsAccepted(isValid);
    onChange({ accepted: isValid, communication: options });
  }, [onChange, setIsAccepted]);

  if (!tc) {
    return null;
  }

  // the tc will always has a fixed value, because we hardcode the aggreement.xml file in UI server
  // here we overwrite the values of the selected options with the values from the user object
  if (!_.isEmpty(communication) && !_.isEmpty(tc.Marketing)) {
    _.each(tc.Marketing, (item) => {
      if (item.Id === 'AllowContact' && _.has(item, 'Selected') && _.has(communication, 'AllowContact')) {
        const updatedItem = { ...item, Selected: communication.AllowContact };
        Object.assign(item, updatedItem);
      }
      if (item.Id === 'AllowContactByPhone' && _.has(item, 'Selected') && _.has(communication, 'AllowContactByPhone')) {
        const updatedItem = { ...item, Selected: communication.AllowContactByPhone };
        Object.assign(item, updatedItem);
      }
    });
  }

  return (
    <>
      <Row>
        <Col sm={12}>
          <TermsAndConditionsComponent
            ccBaseUrl={TermsAndConditionsUrl}
            i18n={legacyI18n}
            marketingModels={tc.Marketing || EmptyArray}
            termsAndConditionsModels={tc.TermsAndConditions || EmptyArray}
            onChange={onTermsChange}
            isValidInitialValue={isTAndCChecked}
            isDisabled={isDisabled}
            country={isMarketingCommunicationDefaultOptInEnabled ? country.toUpperCase() : ''}
          />
        </Col>
      </Row>
      <Row>
        <Col sm={12}>
          {showValidationResult && !isAccepted && validationNote({ i18n })}
        </Col>
      </Row>
    </>
  );
});

TermsAndConditions.propTypes = {
  country: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  showValidationResult: PropTypes.bool.isRequired,
  isTAndCChecked: PropTypes.bool,
  isDisabled: PropTypes.bool,
  communication: PropTypes.shape({
    AllowContact: PropTypes.bool,
    AllowContactByPhone: PropTypes.bool,
  }),

};

TermsAndConditions.defaultProps = {
  isTAndCChecked: false,
  isDisabled: false,
  communication: {},
};

function validationNote({ i18n }) {
  return (
    <span className="tc-validation-note">
      {i18n.getString(_TL_('Please accept the Terms and Conditions to continue.'))}
    </span>
  );
}
